<template>
    <input
        type="text"
        :class="[inputStyle]"
        v-model="amount"
        :id="id"
        :min="minNumber"
        :max="maxNumber"
        :disabled="!editable"
        @input="inputHandler($event.target.value)"
        @change="changeHandler($event.target.value)"
        @focus="setIsEditing(true)"
        @blur="setIsEditing(false)"
    />
</template>

<script>
export default {
    components: {},
    props: {
        decimal: {
            type: Number,
            default: 0,
        },
        negative: {
            type: Boolean,
            default: true,
        },
        min: {
            type: Number,
        },
        max: {
            type: Number,
        },
        editable: {
            type: Boolean,
            default: true,
        },
        id: {
            type: String,
            default: "",
        },
        value: {
            type: Number,
            default: 0,
        },
        dynamicDecimalPlace: {
            type: Boolean,
            default: false,
        },
        dynamicMinDecimalPlace: {
            type: Number,
            default: 4,
        },
        nullable: {
            type: Boolean,
            default: false,
        },
        inputStyle: {
            type: [String, Array],
            default: "",
        },
    },

    data() {
        return {
            amount: 0,
            isEditing: false,
        };
    },

    watch: {
        value: {
            immediate: true,
            handler(n, o) {
                if (!this.isEditing) {
                    this.amount = this.parseNumber(n);
                }
            },
        },
        decimal: {
            immediate: true,
            handler(n) {
                this.amount = this.parseNumber(this.value);
            },
        },
    },

    model: {
        prop: "value",
        event: "change",
    },

    computed: {
        isPositive: function () {
            return this.negative && this.value > 0;
        },
        isNegative: function () {
            return this.negative && this.value < 0;
        },
        minNumber: function () {
            const min = this.min ?? Number.MIN_SAFE_INTEGER;

            if (min < 0) {
                return this.negative ? min : 0;
            }

            return min;
        },
        maxNumber: function () {
            return this.max ?? Number.MAX_SAFE_INTEGER;
        },
    },

    methods: {
        setIsEditing(value) {
            this.isEditing = value;
        },

        parseNumber(value) {
            if (typeof value !== "number") {
                return "";
            }

            if (this.dynamicDecimalPlace) {
                return this.dynamicDecimalPlaceValue(value);
            } else {
                let factor = Math.pow(10, this.decimal);
                return Number(Math.round(value * factor) / factor).toFixed(this.decimal);
            }
        },

        changedValue(value = null, type) {
            if (this.value != value && this.isNumeric(value)) {
                let factor = Math.pow(10, this.decimal);
                value = parseFloat(Number(Math.round(value * factor) / factor).toFixed(this.decimal));
            }

            if (type == "input") {
                this.$emit("input", value);
            } else {
                this.$emit("change", value);
            }
        },

        // dynamicDecimalPlaceValue(value) {
        //     let newValue = value;
        //     let isRemainderEqualZero = false;
        //     let decimalPlaceCount = 0;
        //     let factor = Math.pow(10, 6);

        //     while (isRemainderEqualZero == false && decimalPlaceCount < this.dynamicMinDecimalPlace) {
        //         newValue = Math.floor(newValue * factor) / factor;

        //         if (newValue % 1 == 0) {
        //             isRemainderEqualZero = true;
        //         } else {
        //             decimalPlaceCount++;
        //             newValue = (newValue * 10).toFixed(6);
        //         }
        //     }

        //     factor = Math.pow(10, decimalPlaceCount);
        //     return Number(Math.floor(value * factor) / factor);
        // },

        dynamicDecimalPlaceValue(value) {
            if (this.dynamicMinDecimalPlace === 0 && this.decimal === 0) {
                return Math.floor(value); // Truncate the number to an integer without any decimal places
            }

            // Create a factor to truncate the number without rounding
            const factor = Math.pow(10, this.decimal);

            // Truncate the number without rounding
            let truncatedNum = Math.floor(parseFloat((value * factor).toFixed(6))) / factor;

            // Convert the truncated number to a string with fixed maximum decimal places
            let str = truncatedNum.toFixed(this.decimal);

            // Remove trailing zeros beyond the minimum decimal places
            let regex = new RegExp(`(\\.\\d{${this.dynamicMinDecimalPlace}}\\d*?)0+$`);
            str = str.replace(regex, "$1");

            // Ensure at least the minimum number of decimal places
            if (this.dynamicMinDecimalPlace > 0) {
                if (str.indexOf(".") === -1) {
                    str += ".";
                }
                while (str.length - str.indexOf(".") - 1 < this.dynamicMinDecimalPlace) {
                    str += "0";
                }
            }
            return str;
        },

        changeHandler(value) {
            this.amount = "";

            if (!this.isNumeric(value)) {
                if (this.nullable) {
                    this.changedValue(null, "change");
                } else {
                    this.amount = this.parseNumber(0);
                    this.changedValue(0, "change");
                }
            } else {
                const factor = Math.pow(10, this.decimal);
                let truncatedValue = Math.floor((this.process(value) * factor).toPrecision(15)) / factor;
                this.amount = this.parseNumber(truncatedValue);
                this.changedValue(truncatedValue, "change");
            }
        },

        inputHandler(value) {
            if (value !== "") {
                const factor = Math.pow(10, this.decimal);
                let truncatedValue = Math.floor((this.process(value) * factor).toPrecision(15)) / factor;

                this.changedValue(truncatedValue, "input");
            } else if (this.nullable) {
                this.changedValue(null, "input");
            } else {
                this.changedValue(0, "input");
            }
        },

        process(value) {
            if (!this.isNumeric(value)) {
                if (this.nullable) {
                    return null;
                } else {
                    return 0;
                }
            } else {
                if (value >= this.maxNumber) return Number(this.maxNumber);
                if (value <= this.minNumber) return Number(this.minNumber);
                if (value > this.minNumber && value < this.maxNumber) return Number(value);
                if (!this.minus && value < 0) {
                    value = this.minNumber >= 0 ? Number(this.minNumber) : 0;
                }

                return value;
            }
        },

        isNumeric(value) {
            return !isNaN(parseFloat(value)) && isFinite(value);
        },
    },
};
</script>
